import React from "react";
import { toast } from "sonner";
import { Mail, Trash2, CheckCircle2 } from "lucide-react";
import { useData } from "../../context/DataContext";
import { formatDateTime } from "../../lib/format";

const STATUS_TONES = {
    new: "bg-saffron-50 text-saffron-700",
    read: "bg-slate-100 text-ink-light",
    resolved: "bg-teal-50 text-teal-700",
};

const Inquiries = () => {
    const { inquiries, updateInquiry, deleteInquiry } = useData();

    const setStatus = (id, status) => {
        updateInquiry(id, { status });
        toast.success(`Marked ${status}.`);
    };
    const remove = (id) => {
        if (window.confirm("Delete this inquiry?")) {
            deleteInquiry(id);
            toast.success("Inquiry deleted.");
        }
    };

    return (
        <div data-testid="admin-inquiries-page">
            <h1 className="font-display text-3xl text-ink">Customer inquiries</h1>
            <p className="text-ink-light text-sm mt-1">{inquiries.length} total messages</p>

            <div className="mt-6 space-y-3">
                {inquiries.length === 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-ink-light text-sm">
                        No inquiries yet. Form submissions from the contact page will appear here.
                    </div>
                )}
                {inquiries.map((i) => (
                    <div key={i.id} className="bg-white border border-slate-200 rounded-xl p-5">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-ink">{i.name}</span>
                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${STATUS_TONES[i.status] || STATUS_TONES.new}`}>{i.status}</span>
                                </div>
                                <div className="text-xs text-ink-light mt-0.5">{i.email} · {i.phone || "—"}</div>
                                {i.subject && <div className="text-sm font-medium text-ink mt-2">{i.subject}</div>}
                                <p className="text-sm text-ink-light mt-1 leading-relaxed">{i.message}</p>
                                <div className="text-xs text-ink-light mt-3">{formatDateTime(i.createdAt)}</div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <a href={`mailto:${i.email}?subject=Re: ${encodeURIComponent(i.subject || "Your SouthTrip inquiry")}`} className="px-3 py-2 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-xs font-semibold flex items-center gap-1.5" data-testid={`reply-${i.id}`}>
                                    <Mail className="w-3.5 h-3.5" /> Reply
                                </a>
                                {i.status !== "resolved" && (
                                    <button onClick={() => setStatus(i.id, "resolved")} className="px-3 py-2 rounded-lg border border-slate-200 hover:border-teal-300 text-ink text-xs font-semibold flex items-center gap-1.5">
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
                                    </button>
                                )}
                                {i.status === "new" && (
                                    <button onClick={() => setStatus(i.id, "read")} className="px-3 py-2 rounded-lg border border-slate-200 text-ink text-xs font-semibold">
                                        Mark read
                                    </button>
                                )}
                                <button onClick={() => remove(i.id)} className="p-2 rounded-md hover:bg-red-50 text-red-600">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Inquiries;
